/**
 * Converts between ProseMirror document and manual JSON format.
 */
import type { Node as ProseNode, Fragment, Mark } from 'prosemirror-model';
import type { EditorState } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { manualSchema } from './schema';

function getXamixTextFromDOM(view: EditorView, pos: number): string | null {
  try {
    const dom = view.nodeDOM(pos);
    if (!dom || !(dom instanceof Element)) return null;
    const el =
      dom.localName === 'xamix-element'
        ? (dom as HTMLElement)
        : (dom as Element).querySelector?.('xamix-element');
    if (!el) return null;
    const val =
      (el as HTMLElement & { textInput?: string }).textInput ??
      el.getAttribute?.('text-input');
    return val ?? null;
  } catch {
    return null;
  }
}

export type InlinePart =
  | { type: 'text'; content: string; bold?: boolean; italic?: boolean; color?: string; highlight?: string }
  | { type: 'xamix'; content: string; bold?: boolean; brush?: boolean; color?: string }
  | { type: 'image'; src: string; alt?: string; title?: string; width?: number | null; height?: number | null }
  | { type: 'svg'; content: string; width?: number | null; height?: number | null }
  | { type: 'heading'; parts?: InlinePart[]; level?: 1 | 2 | 3; content?: string };

export interface ManualBlock {
  type: 'text' | 'xamix' | 'xamix_block' | 'heading' | 'centered' | 'ordered_list' | 'bullet_list' | 'page_break' | 'table' | 'image' | 'svg';
  content?: string;
  /** When present, preserves inline xamix in heading/centered blocks */
  parts?: InlinePart[];
  /** For ordered_list and bullet_list: each item's inline content */
  items?: InlinePart[][];
  /** For table: each row is an array of cell contents (each cell = InlinePart[]) */
  rows?: InlinePart[][][];
  /** For table: CSS margin value (e.g. "1em", "1em 2em") */
  margin?: string;
  /** For table: table background color (CSS color) */
  backgroundColor?: string;
  /** For table: table border color (CSS color) */
  borderColor?: string;
  /** For table: per-cell background colors, same shape as rows (row index -> cell index -> color or null) */
  cellBackgrounds?: (string | null)[][];
  /** For table: per-cell colspan/rowspan, same shape as rows (row index -> cell index -> span info or null) */
  cellSpans?: ({ colspan?: number; rowspan?: number } | null)[][];
  /** For table: column widths in pixels (one per column), from resizing; applied in print */
  colWidths?: number[];
  /** Heading level (1-3) for type heading; xamix block style (0=normal, 1-3=heading) for type xamix */
  level?: 0 | 1 | 2 | 3;
  size?: 'small' | 'normal' | 'large';
  bold?: boolean;
  brush?: boolean;
  /** Text color (CSS color) for text, xamix, xamix_block */
  color?: string;
  /** Highlight/marker background color for text */
  highlight?: string;
  /** For image blocks: image source URL */
  src?: string;
  /** For image blocks: alt text */
  alt?: string;
  /** For image blocks: title text */
  title?: string;
  /** For image/svg blocks: width in pixels */
  width?: number | null;
  /** For image/svg blocks: height in pixels */
  height?: number | null;
  italic?: boolean;
}

export interface ManualSection {
  title?: string;
  /** Page/section background color (CSS color) */
  backgroundColor?: string;
  blocks: ManualBlock[];
}

export interface ManualContent {
  title: string;
  sections: ManualSection[];
}

function createTextNodes(content: string, bold?: boolean, italic?: boolean, color?: string, highlight?: string): ProseNode[] {
  const marks: Mark[] = [];
  if (bold) marks.push(manualSchema.marks.bold.create());
  if (italic) marks.push(manualSchema.marks.italic.create());
  // When both color and highlight are present, combine them in the highlight mark
  if (highlight && manualSchema.marks.highlight) {
    marks.push(manualSchema.marks.highlight.create({ color: highlight, textColor: color || null }));
  } else if (color && manualSchema.marks.textColor) {
    marks.push(manualSchema.marks.textColor.create({ color }));
  }
  const raw = (content ?? '') || '\u200B';
  if (!raw.includes('\n')) {
    return [manualSchema.text(raw, marks.length > 0 ? marks : undefined)];
  }
  const segments = raw.split('\n');
  const nodes: ProseNode[] = [];
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].length > 0) {
      nodes.push(manualSchema.text(segments[i], marks.length > 0 ? marks : undefined));
    }
    if (i < segments.length - 1) {
      nodes.push(manualSchema.nodes.hard_break.create());
    }
  }
  return nodes.length > 0 ? nodes : [manualSchema.text('\u200B', marks.length > 0 ? marks : undefined)];
}

function inlinePartToNodes(p: InlinePart): ProseNode[] {
  if (p.type === 'xamix') {
    return [manualSchema.nodes.xamix.create({ textInput: p.content, bold: !!p.bold, brush: !!p.brush, color: (p as { color?: string }).color || null })];
  }
  if (p.type === 'image') {
    return [manualSchema.nodes.inline_image.create({ src: p.src, alt: p.alt || '', title: p.title || '', width: p.width ?? null, height: p.height ?? null })];
  }
  if (p.type === 'svg') {
    return [manualSchema.nodes.inline_svg.create({ content: p.content, width: p.width ?? null, height: p.height ?? null })];
  }
  if (p.type === 'heading') {
    const content = p.parts
      ? p.parts.flatMap(inlinePartToNodes)
      : p.content
        ? createTextNodes(p.content)
        : [];
    return [manualSchema.nodes.heading.create({ level: (p.level ?? 1) as 1 | 2 | 3 }, content)];
  }
  return createTextNodes(p.content, (p as { bold?: boolean }).bold, (p as { italic?: boolean }).italic, (p as { color?: string }).color, (p as { highlight?: string }).highlight);
}

function getTextContent(node: ProseNode): string {
  let out = '';
  node.descendants((n) => {
    if (n.isText) out += n.text ?? '';
    return true;
  });
  return out;
}

function getMarksFromNode(node: ProseNode): { bold: boolean; italic: boolean; color?: string; highlight?: string } {
  const bold = node.marks.some((m) => m.type.name === 'bold');
  const italic = node.marks.some((m) => m.type.name === 'italic');
  const highlightMark = node.marks.find((m) => m.type.name === 'highlight');
  // Get color from highlight mark's textColor attribute if present, otherwise from textColor mark
  const color = (highlightMark?.attrs?.textColor as string | undefined) 
    || (node.marks.find((m) => m.type.name === 'textColor')?.attrs?.color as string | undefined);
  const highlight = highlightMark?.attrs?.color as string | undefined;
  return { bold, italic, color, highlight };
}

function textPartMatches(a: InlinePart, b: { bold?: boolean; italic?: boolean; color?: string; highlight?: string }): boolean {
  if (a.type !== 'text') return false;
  const aColor = (a as { color?: string }).color ?? '';
  const bColor = (b as { color?: string }).color ?? '';
  const aHl = (a as { highlight?: string }).highlight ?? '';
  const bHl = (b as { highlight?: string }).highlight ?? '';
  return !!(a.bold) === !!(b.bold) && !!(a.italic) === !!(b.italic) && aColor === bColor && aHl === bHl;
}

function getInlinePartsFromNode(
  node: ProseNode,
  pos: number,
  view?: EditorView | null
): InlinePart[] {
  const parts: InlinePart[] = [];
  node.forEach((child, offset) => {
    if (child.type.name === 'text') {
      const raw = child.text ?? '';
      const text = raw.replace(/\u200B/g, '');
      const marks = getMarksFromNode(child);
      const last = parts[parts.length - 1];
      if (text && last?.type === 'text' && textPartMatches(last, marks)) {
        last.content += text;
      } else if (text) {
        parts.push({ type: 'text', content: text, bold: marks.bold || undefined, italic: marks.italic || undefined, color: marks.color, highlight: marks.highlight });
      } else {
        parts.push({ type: 'text', content: '', bold: marks.bold || undefined, italic: marks.italic || undefined, color: marks.color, highlight: marks.highlight });
      }
    } else if (child.type.name === 'hard_break') {
      const last = parts[parts.length - 1];
      if (last?.type === 'text') {
        last.content += '\n';
      } else {
        parts.push({ type: 'text', content: '\n' });
      }
    } else if (child.type.name === 'xamix') {
      const childPos = pos + 1 + offset;
      const fromDOM = view ? getXamixTextFromDOM(view, childPos) : null;
      const xColor = (child.attrs as { color?: string | null }).color;
      parts.push({ type: 'xamix', content: fromDOM ?? child.attrs.textInput ?? '', bold: !!(child.attrs.bold as boolean), brush: !!(child.attrs.brush as boolean), color: xColor && xColor !== '' ? xColor : undefined });
    } else if (child.type.name === 'inline_image') {
      parts.push({ type: 'image', src: child.attrs.src ?? '', alt: child.attrs.alt || undefined, title: child.attrs.title || undefined, width: child.attrs.width ?? null, height: child.attrs.height ?? null });
    } else if (child.type.name === 'inline_svg') {
      parts.push({ type: 'svg', content: child.attrs.content ?? '', width: child.attrs.width ?? null, height: child.attrs.height ?? null });
    }
  });
  return parts;
}

function getListItemParts(para: ProseNode, paraPos: number, view?: EditorView | null): InlinePart[] {
  const parts: InlinePart[] = [];
  para.forEach((child, offset) => {
    if (child.type.name === 'text') {
      const raw = child.text ?? '';
      const text = raw.replace(/\u200B/g, '');
      const marks = getMarksFromNode(child);
      const last = parts[parts.length - 1];
      if (text && last?.type === 'text' && textPartMatches(last, marks)) {
        last.content += text;
      } else if (text) {
        parts.push({ type: 'text', content: text, bold: marks.bold || undefined, italic: marks.italic || undefined, color: marks.color, highlight: marks.highlight });
      } else {
        parts.push({ type: 'text', content: '', bold: marks.bold || undefined, italic: marks.italic || undefined, color: marks.color, highlight: marks.highlight });
      }
    } else if (child.type.name === 'hard_break') {
      const last = parts[parts.length - 1];
      if (last?.type === 'text') {
        last.content += '\n';
      } else {
        parts.push({ type: 'text', content: '\n' });
      }
    } else if (child.type.name === 'xamix') {
      const childPos = paraPos + 1 + offset;
      const fromDOM = view ? getXamixTextFromDOM(view, childPos) : null;
      const xColor = (child.attrs as { color?: string | null }).color;
      parts.push({
        type: 'xamix',
        content: fromDOM ?? child.attrs.textInput ?? '',
        bold: !!(child.attrs.bold as boolean),
        brush: !!(child.attrs.brush as boolean),
        color: xColor && xColor !== '' ? xColor : undefined,
      });
    } else if (child.type.name === 'inline_image') {
      parts.push({ type: 'image', src: child.attrs.src ?? '', alt: child.attrs.alt || undefined, title: child.attrs.title || undefined, width: child.attrs.width ?? null, height: child.attrs.height ?? null });
    } else if (child.type.name === 'inline_svg') {
      parts.push({ type: 'svg', content: child.attrs.content ?? '', width: child.attrs.width ?? null, height: child.attrs.height ?? null });
    }
  });
  if (parts.length === 0) {
    const fallback = (getTextContent(para) || '').replace(/\u200B/g, '');
    parts.push({ type: 'text', content: fallback });
  }
  return parts;
}

function nodeToBlocks(node: ProseNode, view?: EditorView | null): ManualBlock[] {
  const blocks: ManualBlock[] = [];

  node.descendants((n, pos) => {
    if (n.type.name === 'ordered_list' || n.type.name === 'bullet_list') {
      const items: InlinePart[][] = [];
      let listPos = pos + 1;
      n.forEach((listItem) => {
        const para = listItem.firstChild;
        const paraPos = listPos + 1;
        if (para?.type.name === 'paragraph') {
          items.push(getListItemParts(para, paraPos, view));
        } else {
          items.push([{ type: 'text', content: getTextContent(listItem) || '' }]);
        }
        listPos += listItem.nodeSize;
      });
      blocks.push({
        type: n.type.name === 'ordered_list' ? 'ordered_list' : 'bullet_list',
        items,
      });
      return false;
    }
    if (n.type.name === 'list_item') {
      return true;
    }
    if (n.type.name === 'paragraph') {
      const parts: ManualBlock[] = [];
      n.forEach((child, offset) => {
        if (child.type.name === 'text') {
          const raw = child.text ?? '';
          const text = raw.replace(/\u200B/g, ''); // normalize ZWS placeholder to empty
          const marks = getMarksFromNode(child);
          const last = parts[parts.length - 1];
          if (text && last?.type === 'text' && textPartMatches(last as InlinePart, marks)) {
            last.content! += text;
          } else if (text) {
            parts.push({ type: 'text', content: text, bold: marks.bold || undefined, italic: marks.italic || undefined, color: marks.color, highlight: marks.highlight });
          } else {
            parts.push({ type: 'text', content: '', bold: marks.bold || undefined, italic: marks.italic || undefined, color: marks.color, highlight: marks.highlight });
          }
        } else if (child.type.name === 'hard_break') {
          const last = parts[parts.length - 1];
          if (last?.type === 'text') {
            last.content! += '\n';
          } else {
            parts.push({ type: 'text', content: '\n' });
          }
        } else if (child.type.name === 'xamix') {
          const childPos = pos + 1 + offset;
          const fromDOM = view ? getXamixTextFromDOM(view, childPos) : null;
          const xColor = (child.attrs as { color?: string | null }).color;
          parts.push({ type: 'xamix', content: fromDOM ?? child.attrs.textInput ?? '', bold: !!(child.attrs.bold as boolean), brush: !!(child.attrs.brush as boolean), color: xColor && xColor !== '' ? xColor : undefined });
        } else if (child.type.name === 'inline_image') {
          parts.push({ type: 'image', src: child.attrs.src ?? '', alt: child.attrs.alt || undefined, title: child.attrs.title || undefined, width: child.attrs.width ?? null, height: child.attrs.height ?? null });
        } else if (child.type.name === 'inline_svg') {
          parts.push({ type: 'svg', content: child.attrs.content ?? '', width: child.attrs.width ?? null, height: child.attrs.height ?? null });
        }
      });
      if (parts.length > 0) {
        if (parts.length === 1 && parts[0].type === 'xamix') {
          blocks.push({ type: 'text', content: '' });
          blocks.push(parts[0]);
        } else {
          blocks.push(...parts);
        }
      } else {
        const fallback = (getTextContent(n) || '').replace(/\u200B/g, '');
        blocks.push({ type: 'text', content: fallback });
      }
      return false;
    }
    if (n.type.name === 'heading') {
      const parts = getInlinePartsFromNode(n, pos, view);
      if (parts.length > 0) {
        blocks.push({
          type: 'heading',
          parts,
          level: (n.attrs.level as 1 | 2 | 3) ?? 1,
        });
      } else {
        blocks.push({
          type: 'heading',
          content: getTextContent(n),
          level: (n.attrs.level as 1 | 2 | 3) ?? 1,
        });
      }
      return false;
    }
    if (n.type.name === 'block_xamix') {
      const fromDOM = view ? getXamixTextFromDOM(view, pos) : null;
      const lvl = (n.attrs.level as number) || 0;
      const xbColor = (n.attrs as { color?: string | null }).color;
      blocks.push({
        type: 'xamix_block',
        content: fromDOM ?? n.attrs.textInput ?? '',
        level: (lvl >= 0 && lvl <= 3 ? lvl : 0) as 0 | 1 | 2 | 3,
        bold: !!(n.attrs.bold as boolean),
        brush: !!(n.attrs.brush as boolean),
        color: xbColor && xbColor !== '' ? xbColor : undefined,
      });
      return false;
    }
    if (n.type.name === 'centered') {
      const parts = getInlinePartsFromNode(n, pos, view);
      if (parts.length > 0) {
        blocks.push({
          type: 'centered',
          parts,
          size: (n.attrs.size as 'small' | 'normal' | 'large') ?? 'normal',
        });
      } else {
        blocks.push({
          type: 'centered',
          content: getTextContent(n),
          size: (n.attrs.size as 'small' | 'normal' | 'large') ?? 'normal',
        });
      }
      return false;
    }
    if (n.type.name === 'page_break') {
      blocks.push({ type: 'page_break' });
      return false;
    }
    if (n.type.name === 'table') {
      const rows: InlinePart[][][] = [];
      const cellBackgrounds: (string | null)[][] = [];
      const cellSpans: ({ colspan?: number; rowspan?: number } | null)[][] = [];
      let colWidths: number[] | undefined;
      // First check if colWidths is stored in the table node's attrs
      const tableColWidths = (n.attrs as { colWidths?: number[] | null }).colWidths;
      if (tableColWidths && Array.isArray(tableColWidths) && tableColWidths.length > 0) {
        colWidths = [...tableColWidths];
      }
      let rowPos = pos + 1;
      n.forEach((rowNode, rowIdx) => {
        const rowCells: InlinePart[][] = [];
        const rowColors: (string | null)[] = [];
        const rowSpans: ({ colspan?: number; rowspan?: number } | null)[] = [];
        let cellPos = rowPos + 1;
        rowNode.forEach((cellNode) => {
          
          // Collect ALL inline content from ALL children of the cell, not just firstChild
          const allInlineParts: InlinePart[] = [];
          cellNode.forEach((child, offset) => {
            const contentPos = cellPos + offset + 1;
            if (child.type.name === 'paragraph') {
              const parts = getInlinePartsFromNode(child, contentPos, view);
              if (allInlineParts.length > 0 && parts.length > 0) {
                // Add a newline between block elements
                allInlineParts.push({ type: 'text', content: '\n' });
              }
              allInlineParts.push(...parts);
            } else if (child.type.name === 'heading') {
              // For headings, get the inline parts directly from the heading's children
              const headingParts: InlinePart[] = [];
              child.forEach((hc, hOffset) => {
                if (hc.type.name === 'text') {
                  const raw = hc.text ?? '';
                  const text = raw.replace(/\u200B/g, '');
                  const marks = getMarksFromNode(hc);
                  if (text) {
                    headingParts.push({ type: 'text', content: text, bold: marks.bold || undefined, italic: marks.italic || undefined, color: marks.color, highlight: marks.highlight });
                  } else if (headingParts.length === 0) {
                    // Empty heading - add empty text placeholder
                    headingParts.push({ type: 'text', content: '' });
                  }
                } else if (hc.type.name === 'xamix') {
                  const childPos = contentPos + 1 + hOffset;
                  const fromDOM = view ? getXamixTextFromDOM(view, childPos) : null;
                  const xColor = (hc.attrs as { color?: string | null }).color;
                  headingParts.push({ type: 'xamix', content: fromDOM ?? hc.attrs.textInput ?? '', bold: !!(hc.attrs.bold as boolean), brush: !!(hc.attrs.brush as boolean), color: xColor && xColor !== '' ? xColor : undefined });
                } else if (hc.type.name === 'inline_image') {
                  headingParts.push({ type: 'image', src: hc.attrs.src ?? '', alt: hc.attrs.alt || undefined, title: hc.attrs.title || undefined, width: hc.attrs.width ?? null, height: hc.attrs.height ?? null });
                } else if (hc.type.name === 'inline_svg') {
                  headingParts.push({ type: 'svg', content: hc.attrs.content ?? '', width: hc.attrs.width ?? null, height: hc.attrs.height ?? null });
                }
              });
              if (allInlineParts.length > 0 && headingParts.length > 0) {
                allInlineParts.push({ type: 'text', content: '\n' });
              }
              allInlineParts.push({ type: 'heading' as const, parts: headingParts, level: (child.attrs.level as 1 | 2 | 3) ?? 1 });
            }
          });
          // If no children found, create empty text
          if (allInlineParts.length === 0) {
            allInlineParts.push({ type: 'text', content: '' });
          }
          rowCells.push(allInlineParts);
          const cellBg = (cellNode.attrs as { backgroundColor?: string | null }).backgroundColor;
          rowColors.push(cellBg && cellBg !== '' ? cellBg : null);
          // Read colspan and rowspan from cell attributes
          // Only save colspan/rowspan if they're > 1 (actual cell merging)
          const colspan = (cellNode.attrs as { colspan?: number }).colspan;
          const rowspan = (cellNode.attrs as { rowspan?: number }).rowspan;
          const hasColspan = colspan !== undefined && colspan > 1;
          const hasRowspan = rowspan !== undefined && rowspan > 1;
          rowSpans.push(hasColspan || hasRowspan ? { colspan: hasColspan ? colspan : undefined, rowspan: hasRowspan ? rowspan : undefined } : null);
          // Only read colWidths from cells if we don't already have them from table attrs
          if (!colWidths && rowIdx === 0) {
            const cw = (cellNode.attrs as { colwidth?: number[] | null }).colwidth;
            if (cw && Array.isArray(cw)) {
              colWidths = colWidths ?? [];
              colWidths.push(...cw);
            }
          }
          cellPos += cellNode.nodeSize;
        });
        rows.push(rowCells);
        cellBackgrounds.push(rowColors);
        cellSpans.push(rowSpans);
        rowPos += rowNode.nodeSize;
      });
      const margin = (n.attrs as { margin?: string }).margin;
      const backgroundColor = (n.attrs as { backgroundColor?: string }).backgroundColor;
      const borderColor = (n.attrs as { borderColor?: string }).borderColor;
      blocks.push({
        type: 'table',
        rows,
        margin: margin && margin !== '' ? margin : undefined,
        backgroundColor: backgroundColor && backgroundColor !== '' ? backgroundColor : undefined,
        borderColor: borderColor && borderColor !== '' ? borderColor : undefined,
        cellBackgrounds,
        cellSpans,
        colWidths: colWidths && colWidths.length > 0 ? colWidths : undefined,
      });
      return false;
    }
    return true;
  });

  return blocks;
}

export function editorStateToBlocks(state: EditorState, view?: EditorView | null): ManualBlock[] {
  return nodeToBlocks(state.doc, view);
}

export function blocksFromSlice(slice: { content: Fragment }, view?: EditorView | null): ManualBlock[] {
  if (slice.content.size === 0) return [];
  const tempDoc = manualSchema.topNodeType.create(null, slice.content);
  return nodeToBlocks(tempDoc, view);
}

export function blocksToSections(blocks: ManualBlock[]): ManualSection[] {
  const sections: ManualSection[] = [];
  let current: ManualBlock[] = [];
  for (const b of blocks) {
    if (b.type === 'page_break') {
      sections.push({ blocks: current });
      current = [];
    } else {
      current.push(b);
    }
  }
  sections.push({ blocks: current });
  return sections;
}

/** Normalize content so sections = pages. Splits single section with page_break into multiple sections. */
export function normalizeContentAsPages(content: ManualContent): ManualContent {
  if (content.sections.length === 0) {
    return { title: content.title, sections: [{ blocks: [] }] };
  }
  if (content.sections.length > 1) return content;
  const hasPageBreak = content.sections[0].blocks.some((b) => b.type === 'page_break');
  if (!hasPageBreak) return content;
  const firstBg = content.sections[0]?.backgroundColor;
  const sections = blocksToSections(content.sections[0].blocks);
  if (firstBg && sections.length > 0) {
    sections[0] = { ...sections[0], backgroundColor: firstBg };
  }
  return { title: content.title, sections };
}

export function editorStateToManual(
  state: EditorState,
  title: string = 'Manual',
  view?: EditorView | null
): ManualContent {
  const blocks = nodeToBlocks(state.doc, view);
  const sections = blocksToSections(blocks);
  return { title, sections };
}

function blocksToDocNodes(blocks: ManualBlock[]): ProseNode[] {
  const nodes: ProseNode[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
      if (block.type === 'heading') {
        const content = block.parts
          ? block.parts.flatMap(inlinePartToNodes)
          : block.content
            ? createTextNodes(block.content, block.bold, block.italic, block.color, block.highlight)
            : [];
        nodes.push(manualSchema.nodes.heading.create({ level: ((block.level ?? 1) as 1 | 2 | 3) || 1 }, content));
        i++;
      } else if (block.type === 'centered') {
        const content = block.parts
          ? block.parts.flatMap(inlinePartToNodes)
          : block.content
            ? createTextNodes(block.content, block.bold, block.italic, block.color, block.highlight)
            : [];
        nodes.push(manualSchema.nodes.centered.create({ size: block.size ?? 'normal' }, content));
        i++;
      } else if (block.type === 'xamix_block' || block.type === 'xamix') {
        const lvl = (block.level ?? 0) as 0 | 1 | 2 | 3;
        nodes.push(manualSchema.nodes.block_xamix.create({ textInput: block.content ?? '', level: lvl, bold: !!block.bold, brush: !!block.brush, color: block.color || null }));
        i++;
      } else if (block.type === 'page_break') {
        nodes.push(manualSchema.nodes.page_break.create());
        i++;
      } else if (block.type === 'ordered_list' || block.type === 'bullet_list') {
        const listItems = (block.items ?? []).map((itemParts) => {
          const content = (itemParts ?? []).flatMap(inlinePartToNodes);
          const para = manualSchema.nodes.paragraph.create(null, content.length > 0 ? content : [manualSchema.text('\u200B')]);
          return manualSchema.nodes.list_item.create(null, para);
        });
        const listNode = block.type === 'ordered_list'
          ? manualSchema.nodes.ordered_list.create(null, listItems)
          : manualSchema.nodes.bullet_list.create(null, listItems);
        nodes.push(listNode);
        i++;
      } else if (block.type === 'table') {
        const cellBackgrounds = block.cellBackgrounds ?? [];
        let cellSpans = block.cellSpans ?? [];
        const colWidths = block.colWidths;
        
        // Only infer cellSpans if cellSpans is completely missing
        // Don't infer anything - trust the saved cellSpans or leave empty
        if (cellSpans.length === 0 && (block.rows?.length ?? 0) > 0) {
          // Initialize with empty arrays matching row count
          cellSpans = block.rows!.map(() => []);
        }
        
        // Validate cellSpans has same row count as rows
        if (cellSpans.length !== (block.rows?.length ?? 0)) {
          cellSpans = block.rows!.map(() => []);
        }
        
        const tableRows = (block.rows ?? []).map((rowCells, rowIdx) => {
          const cells = rowCells.map((cellParts, colIdx) => {
            // Convert all cell parts to nodes, converting newlines to hard_break
            const content: ProseNode[] = [];
            for (const part of cellParts ?? []) {
              if (part.type === 'text' && part.content === '\n') {
                // Convert newline to hard_break
                content.push(manualSchema.nodes.hard_break.create());
              } else if (part.type === 'heading') {
                // For heading in cell, create heading node
                const headingContent = part.parts
                  ? part.parts.flatMap(inlinePartToNodes)
                  : part.content
                    ? createTextNodes(part.content)
                    : [];
                content.push(manualSchema.nodes.heading.create({ level: (part.level ?? 1) as 1 | 2 | 3 }, headingContent));
              } else {
                const nodes = inlinePartToNodes(part);
                content.push(...nodes);
              }
            }
            // Create cell content - use direct block content (not wrapped in paragraph) since table cells support block+
            let cellContent: ProseNode;
            if (content.length === 0) {
              // Empty cell - use paragraph with zero-width space
              cellContent = manualSchema.nodes.paragraph.create(null, [manualSchema.text('\u200B')]);
            } else if (content.length === 1 && content[0].type.name === 'heading') {
              // Single heading - don't wrap in paragraph, use as direct cell content
              cellContent = content[0];
            } else if (content.length === 1 && content[0].type.name === 'paragraph') {
              // Single paragraph - use as is
              cellContent = content[0];
            } else {
              // Multiple content blocks - wrap in paragraph
              cellContent = manualSchema.nodes.paragraph.create(null, content);
            }
            
            const cellBg = cellBackgrounds[rowIdx]?.[colIdx] ?? null;
            const span = cellSpans[rowIdx]?.[colIdx];
            const w = colWidths && colWidths[colIdx] != null ? [colWidths[colIdx]] : null;
            return manualSchema.nodes.table_cell.create(
              { backgroundColor: cellBg, colwidth: w, colspan: span?.colspan, rowspan: span?.rowspan },
              cellContent
            );
          });
          return manualSchema.nodes.table_row.create(null, cells);
        });
        if (tableRows.length > 0) {
          const margin = block.margin && block.margin !== '' ? block.margin : '';
          const backgroundColor = block.backgroundColor && block.backgroundColor !== '' ? block.backgroundColor : '';
          const borderColor = block.borderColor && block.borderColor !== '' ? block.borderColor : '';
          nodes.push(manualSchema.nodes.table.create({ margin, backgroundColor, borderColor, colWidths }, tableRows));
        }
        i++;
      } else {
        // text: collect consecutive text/xamix into one paragraph
        const paragraphContent: ProseNode[] = [];
        while (i < blocks.length) {
          const b = blocks[i];
          if (b.type === 'text' || b.type === 'xamix' || b.type === 'image' || b.type === 'svg') {
            paragraphContent.push(...inlinePartToNodes(b as InlinePart));
            i++;
          } else if (b.type === 'page_break') {
            break;
          } else break;
        }
        nodes.push(
          manualSchema.nodes.paragraph.create(null, paragraphContent)
        );
      }
  }

  return nodes;
}

/** Create a ProseMirror doc from a single section's blocks (one page). No page_break in blocks. */
export function manualToDocFromBlocks(blocks: ManualBlock[]): ProseNode {
  const nodes = blocksToDocNodes(blocks.filter((b) => b.type !== 'page_break'));
  const docNodes = nodes.length > 0 ? nodes : [manualSchema.nodes.paragraph.create(null, [])];
  return manualSchema.topNodeType.create(null, docNodes);
}

/** Create a ProseMirror doc from full ManualContent (all sections concatenated). For backwards compat. */
export function manualToDoc(content: ManualContent): ProseNode {
  const allBlocks: ManualBlock[] = [];
  for (const section of content.sections) {
    allBlocks.push(...section.blocks);
  }
  debugger
  const nodes = blocksToDocNodes(allBlocks);
  if (nodes.length === 0) {
    nodes.push(manualSchema.nodes.paragraph.create(null, []));
  }
  return manualSchema.topNodeType.create(null, nodes);
}
